import { JSONSchema6 } from "json-schema";

const createCheckoutSessionSchema: JSONSchema6 =
{
  title: "Create Checkout Session",
  description: "Create a new Amazon Pay Checkout Session to customize and manage the buyer experience, from when the buyer clicks the Amazon Pay button to when they complete checkout.",
  properties: {
    "header": {
      type: "object",
      properties: {
          "x-amz-pay-idempotency-key": {
            title: "x-amz-pay-idempotency-key",
            type: "string"
          },
          "x-amz-pay-simulation-code": {
            title: "x-amz-pay-simulation-code",
            type: "string"
          }
      },
      required: ["x-amz-pay-idempotency-key"]
    },
    "payload": {
      type: "object",
      properties: {
        "storeId": {
          type: "string"
        },
        "chargePermissionType": {
          type: "string",
          enum: ["OneTime", "Recurring", "PaymentMethodOnFile"],
          default: "OneTime"
        },
        "scopes": {
          type: "array",
          "items": {
            type: "string",
            enum: [ "name", "email", "phoneNumber", "shippingAddress", "billingAddress" ]
          },
          maxItems: 5
        },
        "webCheckoutDetails": {
          type: "object",
          properties: {
            checkoutReviewReturnUrl: {
              type: "string",
              maxLength: 512
            },
            checkoutResultReturnUrl: {
              type: "string",
              maxLength: 512
            }
          },
          required: ["checkoutReviewReturnUrl"]
        },
        "recurringMetadata": {
          type: "object",
          properties: {
            "frequency": {
              type: "object",
              properties: {
                "unit": {
                  type: "string",
                  enum: ["", "Year", "Month", "Week", "Day", "Variable"] }
                ,
                "value": {
                  type: "string"
                }
              }
            },
            "amount": {
              type: "object",
              description: "Transaction amount",
              properties: {
                "amount": {
                  type: "string"
                },
                "currencyCode": {
                  type: "string",
                  enum: ["", "AUD", "GBP", "DKK", "EUR", "HKD", "JPY", "NZD", "NOK", "ZAR", "SEK", "CHF", "USD"]
                }
              }
            }
          }
        },
        "deliverySpecifications": {
          type: "object",
          properties: {
            "specialRestrictions": {
              type: "array",
              "items": {
                type: "string",
                enum: [ "RestrictPOBoxes", "RestrictPackstations" ] },
                maxItems: 2
              },
              "addressRestrictions": {
                  type: "object",
                  properties: {
                      type: {
                        type: "string",
                        enum: ["", "Allowed", "NotAllowed"]
                      },
                      "restrictions": {
                          type: "array",
                          items: {
                              type: "object",
                              properties: {
                                  "countryCode": {
                                    type: "string"
                                  },
                                  dependencies: {
                                      properties: {
                                          "statesOrRegions": {
                                            type: "array",
                                            items: {
                                              type: "string"
                                            }
                                          },
                                          "zipCodes": {
                                            type: "array",
                                            items: {
                                              type: "string"
                                            }
                                          }
                                      }
                                  }
                              }
                          }
                      }
                  }
              }
          }
        },
        "paymentDetails": {
          type: "object",
          properties: {
              paymentIntent: {
                type: "string",
                enum: ["Confirm", "Authorize", "AuthorizeWithCapture"]
              },
              "canHandlePendingAuthorization": {
                type: "boolean"
              },
              "chargeAmount": {
                type: "object",
                description: "Transaction amount",
                properties: {
                  "amount": {
                    type: "string"
                  },
                  "currencyCode": {
                    type: "string",
                    enum: ["", "AUD", "GBP", "DKK", "EUR", "HKD", "JPY", "NZD", "NOK", "ZAR", "SEK", "CHF", "USD"]
                  }
                }
              },
              "totalOrderAmount": {
                type: "object",
                description: "Transaction amount",
                properties: {
                  "amount": {
                    type: "string"
                    },
                  "currencyCode": {
                    type: "string",
                    enum: ["", "AUD", "GBP", "DKK", "EUR", "HKD", "JPY", "NZD", "NOK", "ZAR", "SEK", "CHF", "USD"]
                  }
                }
              },
              "presentmentCurrency": {
                type: "string",
                enum: ["", "AUD", "GBP", "DKK", "EUR", "HKD", "JPY", "NZD", "NOK", "ZAR", "SEK", "CHF", "USD"]
              },
              "softDescriptor": {
                type: "string",
                maxLength: 16
              },
              "allowOvercharge": {
                title: "Allow Overcharge",
                type: "boolean"
              },
              "extendExpiration": {
                type: "boolean"
              }
          }
      },
      "merchantMetadata": {
          type: "object",
          properties: {
              "merchantReferenceId": {
                type: "string",
                maxLength: 256
              },
              "merchantStoreName": {
                type: "string",
                maxLength: 50
              },
              "noteToBuyer": {
                type: "string",
                maxLength: 255
              },
              customInformation: {
                type: "string",
                maxLength: 4096
              }
          }
        },
        "platformId": {
          type: "string"
        },
        "providerMetadata": {
          type: "string"
        },
        "paymentMethodOnFileMetadata": {
          type: "object",
          properties: {
            "setupOnly": {
              type: "boolean"
            }
          }
        },
        "chargePermissionId": {
          type: "string"
        }
      },
      required: ["webCheckoutDetails", "storeId"]
    }
  },
  required: ["header", "payload"]
}

export default createCheckoutSessionSchema;
